import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Video, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { Avatar } from '../common';

/**
 * Play a WhatsApp-style ring tone via Web Audio (no asset needed).
 * Ring cadence: 2 short beeps, then silence, repeat.
 * Audio context keeps playing when the tab is backgrounded — unlike visual
 * polling, which browsers throttle. Returns a stop() function.
 */
const playRingtone = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return () => {};
  let ctx;
  try {
    ctx = new AudioCtx();
  } catch { return () => {}; }

  let stopped = false;
  let interval;

  const playBeep = (freq, startOffset, duration) => {
    if (stopped) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = ctx.currentTime + startOffset;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
    gain.gain.linearRampToValueAtTime(0, t + duration);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  };

  const tick = () => {
    // "ring ring" pattern — two beeps 0.25s apart, then pause
    playBeep(880, 0, 0.35);
    playBeep(660, 0.45, 0.35);
  };

  tick();
  interval = setInterval(tick, 2000);

  return () => {
    stopped = true;
    clearInterval(interval);
    try { ctx.close(); } catch {}
  };
};

/**
 * Fire a browser-level Notification so a backgrounded tab still alerts the user.
 * Returns the notification instance so caller can close it.
 */
const showSystemNotification = (caller, pricingMode, price) => {
  if (typeof Notification === 'undefined') return null;
  if (Notification.permission !== 'granted') return null;

  const priceLabel =
    pricingMode === 'free' ? 'Free call' :
    pricingMode === 'per_minute' ? `$${Number(price).toFixed(2)}/min` :
    `$${Number(price).toFixed(2)}`;

  try {
    const n = new Notification(`Incoming video call`, {
      body: `${caller?.display_name || caller?.username || 'Someone'} · ${priceLabel}`,
      icon: caller?.avatar || '/favicon.ico',
      tag: 'incoming-call',
      requireInteraction: true, // don't auto-dismiss
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    return n;
  } catch {
    return null;
  }
};

/**
 * Full-screen ringing UI shown to the CREATOR when a fan calls.
 * Accept → creates GetStream call → parent mounts VideoCallRoom.
 * Decline → dismisses.
 * Auto-declines (local) if backend times out (100s).
 */
const IncomingCallModal = () => {
  const { t } = useTranslation('livestream');
  const { currentCall, acceptCall, declineCall, loading, isFullscreenOpen, closeFullscreen } = useVideoCall();
  const [ringingFor, setRingingFor] = useState(0);
  const stopRingtoneRef = useRef(null);
  const notificationRef = useRef(null);

  // Full-screen ringing UI is opt-in — only shown when the user explicitly
  // opens it (from the toast's expand button or by clicking the chat bubble).
  // The CallToast handles ringtone + notification in the default case.
  const show = isFullscreenOpen
    && currentCall
    && currentCall.status === 'requested'
    && currentCall.my_role === 'recipient';

  useEffect(() => {
    if (!show) { setRingingFor(0); return; }
    const tick = setInterval(() => setRingingFor((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [show]);

  // NOTE: ringtone + OS notification are handled by CallToast so they play
  // regardless of whether the full-screen view is open. We intentionally
  // don't duplicate them here.
  void playRingtone; void showSystemNotification; void stopRingtoneRef; void notificationRef;

  if (!show) return null;

  const caller = currentCall.caller;
  const timeout = currentCall.ring_timeout_seconds || 100;
  const remaining = Math.max(0, timeout - ringingFor);

  const handleAccept = () => acceptCall(currentCall.id);
  const handleDecline = () => declineCall(currentCall.id);

  const priceLabel =
    currentCall.pricing_mode === 'free' ? t('videoCall.common.freeCall') :
    currentCall.pricing_mode === 'per_minute'
      ? `$${Number(currentCall.price).toFixed(2)}/min`
      : `$${Number(currentCall.price).toFixed(2)}`;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between py-16 px-6"
      style={{
        background: 'linear-gradient(180deg, var(--color-primary-600) 0%, var(--color-primary-800) 100%)',
      }}
    >
      <button
        onClick={closeFullscreen}
        title={t('videoCall.common.minimize')}
        className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition-colors"
      >
        <Minimize2 className="w-5 h-5" />
      </button>

      {/* Top */}
      <div className="text-white/80 text-center">
        <p className="text-xs uppercase tracking-widest font-semibold mb-1 flex items-center justify-center gap-1.5">
          <Video className="w-3.5 h-3.5" />
          {t('videoCall.incoming.title')}
        </p>
        <p className="text-[11px] opacity-70">{t('videoCall.incoming.secondsBeforeMissed', { seconds: remaining })}</p>
      </div>

      {/* Caller card with pulsing ring */}
      <div className="flex flex-col items-center">
        <div className="relative mb-6">
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: 'rgba(255,255,255,0.3)' }}
          />
          <span
            className="absolute -inset-3 rounded-full"
            style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.35)' }}
          />
          <div className="relative">
            <Avatar user={caller} size="xl" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-white">
          {caller?.display_name || caller?.username}
        </h2>
        <p className="text-white/70 text-sm mt-1">@{caller?.username}</p>

        <div className="mt-3 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-medium">
          {priceLabel}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-14">
        <button
          onClick={handleDecline}
          disabled={loading}
          className="flex flex-col items-center gap-2 group"
        >
          <div className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-2xl transition-transform group-active:scale-95">
            <PhoneOff className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs text-white/80">{t('videoCall.common.decline')}</span>
        </button>
        <button
          onClick={handleAccept}
          disabled={loading}
          className="flex flex-col items-center gap-2 group"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center shadow-2xl transition-transform group-active:scale-95 animate-pulse">
            <Phone className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs text-white/80">{t('videoCall.common.accept')}</span>
        </button>
      </div>
    </div>
  );
};

export default IncomingCallModal;
